import { z } from "zod";
import { normalizeApiBaseUrl, type FetchLike } from "./shared";

export const deleteAccountBodySchema = z.object({
  confirm: z.literal("DELETE MY ACCOUNT"),
});

export const deleteAccountSuccessSchema = z.object({
  ok: z.literal(true),
});

export const deleteAccountErrorSchema = z.object({
  error: z.string(),
  details: z.unknown().optional(),
});

export type DeleteAccountBody = z.infer<typeof deleteAccountBodySchema>;
export type DeleteAccountSuccess = z.infer<typeof deleteAccountSuccessSchema>;

export function deleteAccountPath(): string {
  return "/api/account";
}

export class AccountApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "AccountApiError";
    this.status = status;
    this.details = details;
  }
}

type DeleteAccountRequestInput = {
  apiBaseUrl: string;
  accessToken: string;
  fetchImpl?: FetchLike;
};

async function parseAccountError(
  response: Response,
  fallbackMessage: string,
): Promise<AccountApiError> {
  const payload = await response.json().catch(() => null);
  const parsed = deleteAccountErrorSchema.safeParse(payload);
  if (parsed.success) {
    return new AccountApiError(
      parsed.data.error,
      response.status,
      parsed.data.details,
    );
  }
  return new AccountApiError(fallbackMessage, response.status);
}

export async function deleteAccountRequest({
  apiBaseUrl,
  accessToken,
  fetchImpl,
}: DeleteAccountRequestInput): Promise<DeleteAccountSuccess> {
  const body = deleteAccountBodySchema.parse({ confirm: "DELETE MY ACCOUNT" });
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${deleteAccountPath()}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseAccountError(response, "Failed to delete account");
  }

  const payload = await response.json().catch(() => null);
  const parsed = deleteAccountSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new AccountApiError("Invalid account delete response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
